import { useMutation } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { EnumActionType } from "@/api/cashier";
import { cancelDepositTransactionMutation } from "@/api/cashier/@tanstack/react-query.gen";
import { useModal } from "@/components/general/base-modal/useModal";
import { LoadingScreen } from "@/components/loading-screen/LoadingScreen";
import { IframeModal } from "@/components/modals/payment-flow-modals/iframe-modal/IframeModal";
import { IframeWithManualApproveModal } from "@/components/modals/payment-flow-modals/iframe-with-manual-approve-modal/IframeWithManualApproveModal";
import { useLogger } from "@/hooks/useLogger";
import { ERoutes } from "@/utils/enums";
import { createPaymentTracker } from "@/utils/payment-tracking";

export const LoadingPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location || {};
  const { accountLoginId } = useParams();
  const logger = useLogger();
  const tracker = useMemo(() => createPaymentTracker(logger), [logger]);

  // True once a success / manual-confirm path has taken over the navigation,
  // so the modal's auto-close `onClose` doesn't fire a spurious cancellation
  // call against a transaction that is no longer in a cancellable state.
  const successHandledRef = useRef(false);

  const { mutate: cancelDepositTransaction } = useMutation({
    ...cancelDepositTransactionMutation(),
  });

  const navigateToChooseMethod = useCallback(() => {
    if (accountLoginId) {
      navigate(`../${accountLoginId}/${ERoutes.ChooseMethod}`, {
        replace: true,
      });
    } else {
      navigate(`../${ERoutes.Accounts}`, { replace: true });
    }
  }, [navigate, accountLoginId]);

  // Fire-and-forget cancel of the in-flight transaction, then navigate the
  // customer to the Thank You Page so the polling there can resolve into the
  // Cancelled state. Used when the customer closes the iframe (X icon) or
  // taps Back in the manual-confirm modal.
  const cancelAndGoToCompletion = useCallback(() => {
    if (!state?.reference_id || !state?.confirm_url) {
      navigateToChooseMethod();
      return;
    }

    // Successful PSP redirects (and "I've Paid" confirmations) already set
    // this flag and navigated to confirm_url themselves, so skip the cancel.
    if (successHandledRef.current) {
      return;
    }

    tracker.deposit.cancellationRequested({
      referenceId: state.reference_id,
      methodId: state.payment_method_id,
      step:
        state.action_type ===
        EnumActionType.EnumActionTypeIframeWithManualConfirm
          ? "iframe_with_manual_confirm"
          : "iframe",
    });

    cancelDepositTransaction({
      path: { referenceID: state.reference_id },
    });

    navigate(state.confirm_url, { replace: true });
  }, [
    state,
    cancelDepositTransaction,
    navigate,
    navigateToChooseMethod,
    tracker,
  ]);

  const { openModal: openIframeModal, modal: iframeModalComponent } = useModal(
    IframeModal,
    cancelAndGoToCompletion
  );
  const {
    openModal: openIframeWithManualApproveModal,
    modal: iframeWithManualApproveModalComponent,
  } = useModal(IframeWithManualApproveModal, cancelAndGoToCompletion);

  useEffect(() => {
    if (!state) {
      // No navigation state — PSP may have redirected the parent frame,
      // or user navigated here directly. Redirect to payment methods page.
      navigateToChooseMethod();
      return;
    }

    if (
      state.action_type === EnumActionType.EnumActionTypeIframeWithManualConfirm
    ) {
      openIframeWithManualApproveModal({
        iframeUrl: state.iframe_url,
        onConfirm: () => {
          successHandledRef.current = true;
          navigate(state.confirm_url);
        },
      });
    } else if (state.action_type === EnumActionType.EnumActionTypeIframe) {
      console.warn(
        "openIframeModal: ",
        state.iframe_url,
        ", ",
        state.payment_method_id,
        state.confirm_url
      );
      openIframeModal({
        iframeUrl: state.iframe_url,
        onSuccess: () => {
          successHandledRef.current = true;
          navigate(state.confirm_url);
        },
      });
    }
  }, [
    state,
    openIframeModal,
    openIframeWithManualApproveModal,
    navigate,
    navigateToChooseMethod,
  ]);
  return (
    <>
      <LoadingScreen
        description={t("deposit.loading_page.connecting_to_payment_provider")}
      />
      {iframeModalComponent}
      {iframeWithManualApproveModalComponent}
    </>
  );
};
